import { useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { useReservationByCode } from '../api/queries.js';
import { ReservationStatus as RS } from '@waw/shared-types';
import './ReservationStatus.css';

const STATUS_LABELS = {
  [RS.PENDING_PAYMENT]: 'En attente de paiement',
  [RS.PENDING_VALIDATION]: 'En attente de validation',
  [RS.CONFIRMED]: 'Confirmée',
  [RS.CANCELLED]: 'Annulée',
  [RS.REJECTED]: 'Refusée',
};

const STATUS_BADGE_CLASS = {
  [RS.PENDING_PAYMENT]: 'badge-pending',
  [RS.PENDING_VALIDATION]: 'badge-pending',
  [RS.CONFIRMED]: 'badge-confirmed',
  [RS.CANCELLED]: 'badge-cancelled',
  [RS.REJECTED]: 'badge-cancelled',
};

// slotDate arrive en timestamp ISO (minuit heure locale Casablanca) — on le formate
// dans le fuseau du parc pour retrouver la date calendaire voulue.
function formatSlotDate(value) {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleDateString('fr-FR', {
      timeZone: 'Africa/Casablanca',
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  } catch {
    return value;
  }
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

function CrossIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  );
}

export default function ReservationStatus() {
  const { code } = useParams();
  const [searchParams] = useSearchParams();
  const payment = searchParams.get('payment'); // 'success' | 'failed' | null

  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  // Après un paiement, on met en avant le remerciement ; le suivi détaillé reste accessible.
  const [showLookup, setShowLookup] = useState(!payment);

  const { data, isLoading, isError, error } = useReservationByCode(
    submitted ? code : null,
    submitted ? email : null
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    if (email.trim() && code) {
      setSubmitted(true);
    }
  };

  return (
    <div className="container reservation-status">
      {payment === 'success' && (
        <div className="card payment-result payment-result-success">
          <div className="payment-result-icon"><CheckIcon /></div>
          <h1 className="payment-result-title heading-display">Merci&nbsp;!</h1>
          <p className="payment-result-lead">
            Paiement confirmé. Votre réservation <strong>{code}</strong> est enregistrée.
          </p>
          <p className="payment-result-note">
            Un email de confirmation vient de vous être envoyé. Notre équipe valide votre créneau très vite —
            vous recevrez un second email dès que c'est bon. À très bientôt&nbsp;!
          </p>
          <div className="payment-result-actions">
            <Link to="/" className="btn btn-primary">Retour à l'accueil</Link>
            {!showLookup && (
              <button className="btn btn-secondary" onClick={() => setShowLookup(true)}>
                Voir le détail
              </button>
            )}
          </div>
        </div>
      )}

      {payment === 'failed' && (
        <div className="card payment-result payment-result-failed">
          <div className="payment-result-icon"><CrossIcon /></div>
          <h1 className="payment-result-title heading-display" style={{ color: 'var(--waw-pink)' }}>Oups…</h1>
          <p className="payment-result-lead">
            Le paiement de la réservation <strong>{code}</strong> n'a pas abouti.
          </p>
          <p className="payment-result-note">
            Aucun montant n'a été débité. Vous pouvez relancer une réservation quand vous le souhaitez.
          </p>
          <div className="payment-result-actions">
            <Link to="/" className="btn btn-primary">Retour à l'accueil</Link>
          </div>
        </div>
      )}

      {showLookup && (
        <>
          <h2 className="reservation-status-heading">Suivi de réservation</h2>

          {!submitted && (
            <div className="card" style={{ padding: 'var(--space-4)' }}>
              <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--font-size-sm)', marginBottom: 'var(--space-3)', textAlign: 'center' }}>
                Entrez votre email pour consulter votre réservation <strong>{code}</strong>
              </p>
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label className="form-label" htmlFor="status-email">Email</label>
                  <input
                    id="status-email"
                    type="email"
                    className="form-input"
                    placeholder="votre@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
                <button type="submit" className="btn btn-primary btn-block">
                  Consulter
                </button>
              </form>
            </div>
          )}

          {submitted && isLoading && (
            <div className="card" style={{ padding: 'var(--space-4)' }}>
              <div className="skeleton" style={{ height: 24, width: '60%', marginBottom: 'var(--space-2)' }} />
              <div className="skeleton" style={{ height: 16, width: '80%', marginBottom: 'var(--space-1)' }} />
              <div className="skeleton" style={{ height: 16, width: '70%', marginBottom: 'var(--space-1)' }} />
              <div className="skeleton" style={{ height: 16, width: '50%' }} />
            </div>
          )}

          {submitted && isError && (
            <div className="card" style={{ padding: 'var(--space-4)', textAlign: 'center' }}>
              <p style={{ color: 'var(--waw-pink)', marginBottom: 'var(--space-2)' }}>
                {error?.status === 404
                  ? 'Réservation introuvable. Vérifiez le code et l\'email.'
                  : error?.status === 429
                    ? 'Trop de tentatives, réessayez dans quelques instants.'
                    : 'Une erreur est survenue.'}
              </p>
              <button className="btn btn-secondary" onClick={() => setSubmitted(false)}>
                Réessayer
              </button>
            </div>
          )}

          {submitted && data && (
            <div className="card" style={{ padding: 'var(--space-4)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-3)' }}>
                <h2 style={{ fontSize: 'var(--font-size-md)', fontWeight: 700 }}>
                  {data.reservation?.reservationCode || code}
                </h2>
                <span className={`badge ${STATUS_BADGE_CLASS[data.reservation?.status] || 'badge-pending'}`}>
                  {STATUS_LABELS[data.reservation?.status] || data.reservation?.status}
                </span>
              </div>

              <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
                <Detail label="Site" value={data.reservation?.site?.displayName} />
                <Detail label="Événement" value={data.reservation?.eventType?.label} />
                <Detail label="Date" value={formatSlotDate(data.reservation?.slotDate)} />
                <Detail label="Heure" value={data.reservation?.startTime} />
                <Detail label="Participants" value={data.reservation?.participantsCount} />
                {data.reservation?.specialRequest && (
                  <Detail label="Demande spéciale" value={data.reservation.specialRequest} />
                )}
              </div>

              <div style={{ marginTop: 'var(--space-3)', paddingTop: 'var(--space-2)', borderTop: '1px solid var(--border-subtle)' }}>
                <button className="btn btn-secondary" onClick={() => setSubmitted(false)}>
                  Nouvelle recherche
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Detail({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 'var(--font-size-sm)' }}>
      <span style={{ color: 'var(--text-muted)' }}>{label}</span>
      <span style={{ fontWeight: 500 }}>{value}</span>
    </div>
  );
}
